import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Select, Text, Textarea } from '@chakra-ui/react';
import { FiClock, FiCheckCircle } from 'react-icons/fi';
import { lcApi } from '../../../services/api/lc';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';
import {
  ALL_CREATOR_DISPUTE_REASONS,
  CREATOR_DISPUTE_REASON_LABELS,
  isCreatorDisputeReason,
} from '@foodfluence/shared';
import type { CreatorDisputeReason, MarketplaceDispute } from '@foodfluence/shared';

const ALL_REASONS = ALL_CREATOR_DISPUTE_REASONS;

/** Read-only recap of the creator's earlier report. */
function ReportRecap(props: { eyebrow: string; dispute: MarketplaceDispute }) {
  const { eyebrow, dispute } = props;
  return (
    <Box px={4} py={4} borderRadius="20px" border="1px solid" borderColor={ff.line}>
      <Text textStyle="eyebrow" color={ff.text2} mb={2}>
        {eyebrow}
      </Text>
      <Text fontSize="15px" fontWeight="600" color={ff.ink}>
        {CREATOR_DISPUTE_REASON_LABELS[dispute.reason as CreatorDisputeReason] ?? dispute.reason}
      </Text>
      <Text fontSize="14px" color={ff.body} mt={1} whiteSpace="pre-wrap">{dispute.description}</Text>
    </Box>
  );
}

function isResolved(status: MarketplaceDispute['status']): boolean {
  return status === 'resolved_canceled' || status === 'resolved_no_action' || status === 'resolved_other';
}

export interface ReportProblemSheetProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  onSubmitted?: () => void;
}

export function ReportProblemSheet({ isOpen, onClose, orderId, onSubmitted }: ReportProblemSheetProps) {
  const [existing, setExisting] = useState<(MarketplaceDispute & { id: string }) | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [filingNew, setFilingNew] = useState(false);
  const [reason, setReason] = useState<CreatorDisputeReason>('other');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingExisting(true);
    setSuccess(false);
    setSubmitError(null);
    setFilingNew(false);

    lcApi
      .getLatestDisputeForOrder(orderId)
      .then((res) => {
        if (cancelled) return;
        const dispute = res.data ?? null;
        setExisting(dispute);
        if (dispute && dispute.status === 'open') {
          setReason(isCreatorDisputeReason(dispute.reason) ? dispute.reason : 'other');
          setDescription(dispute.description);
        } else {
          setReason('other');
          setDescription('');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReason('other');
          setDescription('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, orderId]);

  async function handleSubmit() {
    if (!description.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await lcApi.reportDisputeForOrder(orderId, { reason, description: description.trim() });
      setSuccess(true);
      onSubmitted?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '';
      setSubmitError(msg.includes('under review') ? 'Your dispute is currently under review by our team.' : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const showForm = !existing || existing.status === 'open' || filingNew;

  const title =
    showForm && existing?.status === 'open'
      ? 'Update your report'
      : showForm
        ? 'Report a problem'
        : existing?.status === 'under_review'
          ? 'Dispute under review'
          : 'Dispute resolved';

  const closeButton = (label: string, isDisabled?: boolean) => (
    <Button variant="inkOutline" flex={1} onClick={onClose} isDisabled={isDisabled}>
      {label}
    </Button>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <Flex gap={3} mt={5}>
          {success ? (
            closeButton('Done')
          ) : existing && existing.status === 'under_review' && !filingNew ? (
            closeButton('Close')
          ) : existing && isResolved(existing.status) && !filingNew ? (
            <>
              {closeButton('Close')}
              <Button
                variant="danger"
                flex={1}
                onClick={() => { setFilingNew(true); setReason('other'); setDescription(''); }}
              >
                Report again
              </Button>
            </>
          ) : (
            <>
              {closeButton('Cancel', isSubmitting)}
              <Button
                variant="danger"
                flex={1}
                onClick={() => void handleSubmit()}
                isLoading={isSubmitting}
                loadingText="Submitting…"
                isDisabled={!description.trim()}
              >
                {existing?.status === 'open' ? 'Update report' : 'Submit report'}
              </Button>
            </>
          )}
        </Flex>
      }
    >
      {success ? (
        <Text fontSize="15px" color={ff.body} lineHeight="1.45">
          Your report has been submitted. Our team will review it and follow up shortly.
        </Text>
      ) : loadingExisting ? (
        <Text fontSize="15px" color={ff.text2}>Loading…</Text>
      ) : existing && existing.status === 'under_review' && !filingNew ? (
        /* Under review */
        <Box>
          <Flex align="flex-start" gap={3} px={4} py={4} borderRadius="20px" bg={ff.grey} mb={3}>
            <Box as={FiClock} boxSize="18px" color={ff.ink} mt="2px" flexShrink={0} />
            <Box>
              <Text fontSize="15px" fontWeight="600" color={ff.ink}>Under review</Text>
              <Text fontSize="14px" color={ff.body} mt={0.5}>
                Our team is actively investigating your report. We'll follow up with you soon.
              </Text>
            </Box>
          </Flex>
          <ReportRecap eyebrow="Your report" dispute={existing} />
        </Box>
      ) : existing && isResolved(existing.status) && !filingNew ? (
        /* Resolved */
        <Box>
          <Flex align="flex-start" gap={3} px={4} py={4} borderRadius="20px" bg={ff.limeSurface} mb={3}>
            <Box as={FiCheckCircle} boxSize="18px" color={ff.ink} mt="2px" flexShrink={0} />
            <Box>
              <Text fontSize="15px" fontWeight="600" color={ff.ink}>
                {existing.status === 'resolved_canceled' ? 'Order canceled' : 'Resolved'}
              </Text>
              {existing.resolutionSummary ? (
                <Text fontSize="14px" color={ff.body} mt={0.5} whiteSpace="pre-wrap">{existing.resolutionSummary}</Text>
              ) : (
                <Text fontSize="14px" color={ff.body} mt={0.5}>Your previous report has been resolved.</Text>
              )}
            </Box>
          </Flex>
          <ReportRecap eyebrow="Previous report" dispute={existing} />
        </Box>
      ) : (
        /* Editable form */
        <Box>
          {existing?.status === 'open' && (
            <Box mb={4} px={4} py={3} borderRadius="14px" bg={ff.cream}>
              <Text fontSize="14px" color={ff.body}>
                You already have an open report on this order. You can update it below.
              </Text>
            </Box>
          )}
          <Box mb={4}>
            <Text fontSize="14px" fontWeight="600" color={ff.ink} mb="6px">
              Reason
            </Text>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value as CreatorDisputeReason)}
              isDisabled={isSubmitting}
              h="48px"
              fontSize="16px"
            >
              {ALL_REASONS.map((r) => (
                <option key={r} value={r}>{CREATOR_DISPUTE_REASON_LABELS[r]}</option>
              ))}
            </Select>
          </Box>
          <Box>
            <Text fontSize="14px" fontWeight="600" color={ff.ink} mb="6px">
              Description
            </Text>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue…"
              rows={4}
              isDisabled={isSubmitting}
              fontSize="16px"
              resize="none"
            />
          </Box>
          {submitError && (
            <Text mt={2} fontSize="14px" color={ff.danger}>{submitError}</Text>
          )}
        </Box>
      )}
    </ModalShell>
  );
}
